import { useListCaptionStyles } from '@reelplicate/api-client/caption-styles';
import type { CaptionStyleDto } from '@reelplicate/api-client/model';
import {
  CAPTION_PRESETS,
  type CaptionStyle,
  captionStyleSchema,
  DEFAULT_CAPTION_STYLE,
  presetById,
} from '@reelplicate/flows';

/** What a preview says when there's no caption to show. */
export const SAMPLE_CAPTION =
  'POV: the car ride is the *calmest* part of your wedding day';

export type StyleSource = 'tiktok' | 'instagram' | 'custom';

/** A style to pick: a preset or one of the workspace's own. */
export type StyleOption = {
  ref: string;
  name: string;
  source: StyleSource;
  style: CaptionStyle;
  /** Custom styles: flows using it. */
  usedBy?: number;
};

export const SOURCE_LABELS: Record<StyleSource, string> = {
  tiktok: 'TikTok',
  instagram: 'Instagram',
  custom: 'Your style',
};

export const PRESET_OPTIONS: StyleOption[] = CAPTION_PRESETS.map((p) => ({
  ref: p.id,
  name: p.name,
  source: p.source,
  style: p.style,
}));

export const toOption = (dto: CaptionStyleDto): StyleOption | null => {
  const parsed = captionStyleSchema.safeParse(dto.style);
  return parsed.success
    ? {
        ref: dto.id,
        name: dto.name,
        source: 'custom',
        style: parsed.data,
        usedBy: dto.usedBy,
      }
    : null;
};

/** Every style a workspace can pick, presets first, and a lookup by ref. */
export function useCaptionStyleOptions(workspaceId: string) {
  const { data } = useListCaptionStyles(workspaceId, {
    query: { enabled: Boolean(workspaceId), throwOnError: false },
  });
  const custom = (data ?? []).flatMap((dto) => toOption(dto) ?? []);
  const all = [...PRESET_OPTIONS, ...custom];
  return {
    loaded: data !== undefined,
    presets: PRESET_OPTIONS,
    custom,
    all,
    find: (ref: string | null | undefined) =>
      ref ? all.find((o) => o.ref === ref) : undefined,
  };
}

/** The default look when nothing is picked: the workspace's, else White band. */
export const fallbackOption = (
  find: (ref: string | null | undefined) => StyleOption | undefined,
  workspaceDefault: string | null | undefined,
): StyleOption =>
  find(workspaceDefault) ?? {
    ref: DEFAULT_CAPTION_STYLE,
    name: presetById(DEFAULT_CAPTION_STYLE)!.name,
    source: 'tiktok',
    style: presetById(DEFAULT_CAPTION_STYLE)!.style,
  };
